/**
 * Erwähnungs- und Emoji-Syntax im Markdown-Inhalt (Discord-kompatibel, damit die Brücke nichts umschreiben
 * muss außer den IDs):
 *   <@userId>        Nutzer          <@&roleId>   Rolle          <#channelId>  Kanal
 *   <:name:emojiId>  eigenes Emoji   <a:name:emojiId> animiertes Emoji
 *   @everyone / @here (wörtlich)     <t:unix:style> Zeitstempel (optional, Darstellung im Client)
 */
export const MENTION_PATTERNS = {
  user: /<@!?(\d{1,20})>/g,
  role: /<@&(\d{1,20})>/g,
  channel: /<#(\d{1,20})>/g,
  emoji: /<(a?):([A-Za-z0-9_]{2,32}):(\d{1,20})>/g,
  everyone: /(^|[^\w`])@everyone\b/,
  here: /(^|[^\w`])@here\b/,
  timestamp: /<t:(-?\d{1,13})(?::([tTdDfFR]))?>/g,
} as const;

export const formatUserMention = (id: string): string => `<@${id}>`;
export const formatRoleMention = (id: string): string => `<@&${id}>`;
export const formatChannelMention = (id: string): string => `<#${id}>`;
export const formatEmoji = (name: string, id: string, animated = false): string =>
  `<${animated ? 'a' : ''}:${name}:${id}>`;

export interface ParsedMentions {
  users: string[];
  roles: string[];
  channels: string[];
  emojis: { id: string; name: string; animated: boolean }[];
  everyone: boolean;
  here: boolean;
}

/** Entfernt Codeblöcke/Inline-Code, damit Erwähnungen darin nicht zählen. */
export function stripCode(content: string): string {
  return content.replace(/```[\s\S]*?```/g, ' ').replace(/`[^`\n]*`/g, ' ');
}

export function parseMentions(content: string): ParsedMentions {
  const text = stripCode(content);
  const uniq = (re: RegExp, group = 1): string[] => [...new Set([...text.matchAll(re)].map((m) => m[group] as string))];
  const emojiMap = new Map<string, { id: string; name: string; animated: boolean }>();
  for (const m of text.matchAll(MENTION_PATTERNS.emoji)) {
    const id = m[3] as string;
    if (!emojiMap.has(id)) emojiMap.set(id, { id, name: m[2] as string, animated: m[1] === 'a' });
  }
  return {
    users: uniq(MENTION_PATTERNS.user),
    roles: uniq(MENTION_PATTERNS.role),
    channels: uniq(MENTION_PATTERNS.channel),
    emojis: [...emojiMap.values()],
    everyone: MENTION_PATTERNS.everyone.test(text),
    here: MENTION_PATTERNS.here.test(text),
  };
}

/**
 * Reaktions-Schlüssel: Unicode-Emoji wörtlich, eigenes Emoji als `name:id` (wie Discord-REST).
 * In URLs URL-kodiert übertragen.
 */
export function reactionKey(emoji: { id?: string | null; name: string }): string {
  return emoji.id ? `${emoji.name}:${emoji.id}` : emoji.name;
}

export function parseReactionKey(key: string): { id: string | null; name: string } {
  const m = /^([A-Za-z0-9_]{1,32}):(\d{1,20})$/.exec(key);
  return m ? { name: m[1] as string, id: m[2] as string } : { name: key, id: null };
}
